import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Card } from '../components/Card.tsx';
import { Select } from '../components/Select.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

type GoalKey =
  | 'response_sla'
  | 'billable_pct'
  | 'csat'
  | 'project_on_time'
  | 'team_retention';

type GoalStatus =
  | 'hit'
  | 'miss'
  | 'ineligible'
  | 'not_applicable'
  | 'insufficient_data';

interface GoalResult {
  goalKey: GoalKey;
  periodType: 'monthly' | 'quarterly';
  periodKey: string;
  status: GoalStatus;
  actualValue: number | null;
  detail: string;
  bonusUsd: number;
  bonusIfHitUsd: number;
}

interface MyGoalsResponse {
  agentId: string;
  isFullTime: boolean;
  monthKey: string;
  quarterKey: string;
  isPeriodClosed: { monthly: boolean; quarterly: boolean };
  monthlyGoals: GoalResult[];
  quarterlyGoals: GoalResult[];
  trackedMinutesByMonth: Record<string, number>;
}

interface TeamRetentionResponse {
  quarter: string;
  periodKey: string;
  status: GoalStatus;
  retentionPct: number | null;
  activeAtStart: number;
  cancellations: number;
  bonusFtUsd: number;
  bonusPtUsd: number;
  detail: string;
}

const GOAL_TITLE: Record<GoalKey, string> = {
  response_sla: 'Response time SLA',
  billable_pct: 'Billable %',
  csat: 'CSAT',
  project_on_time: 'Project on-time delivery',
  team_retention: 'Client retention',
};

const GOAL_TARGET: Record<GoalKey, string> = {
  response_sla: '100% of replies within 2h business-hours',
  billable_pct: '≥ 50% of expected hours (full-timers only)',
  csat: '≥ 95% of surveys rated 4 or 5 (min 5 surveys)',
  project_on_time: '100% of projects delivered on or before due date',
  team_retention: 'Zero clients put us on notice this quarter',
};

function fmtUsd(n: number): string {
  return `$${n.toFixed(0)}`;
}

function fmtPct(v: number | null): string {
  if (v === null) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

function StatusPill({ status }: { status: GoalStatus }): React.ReactElement {
  const map: Record<GoalStatus, { label: string; cls: string }> = {
    hit: { label: 'HIT', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
    miss: { label: 'MISS', cls: 'bg-red-100 text-red-800 border-red-300' },
    ineligible: { label: 'INELIGIBLE', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
    not_applicable: { label: 'N/A', cls: 'bg-gray-100 text-gray-700 border-gray-300' },
    insufficient_data: { label: 'NOT ENOUGH DATA', cls: 'bg-gray-100 text-gray-700 border-gray-300' },
  };
  const m = map[status];
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${m.cls}`}>
      {m.label}
    </span>
  );
}

function GoalCard({
  goal,
  isClosed,
}: {
  goal: GoalResult;
  isClosed: boolean;
}): React.ReactElement {
  const hit = goal.status === 'hit';
  return (
    <Card className="!p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-[var(--color-muted)]">
            {GOAL_TITLE[goal.goalKey]}
          </div>
          <div className="mt-1 text-sm text-[var(--color-body)]">{GOAL_TARGET[goal.goalKey]}</div>
        </div>
        <StatusPill status={goal.status} />
      </div>
      <div className="mt-4 flex items-end justify-between gap-4">
        <div>
          <div className="text-2xl font-semibold text-[var(--color-header)]">
            {goal.actualValue === null ? '—' : fmtPct(goal.actualValue)}
          </div>
          <div className="mt-1 text-xs text-[var(--color-muted)]">{goal.detail}</div>
        </div>
        <div className="text-right">
          <div className={`text-xl font-bold ${hit ? 'text-emerald-700' : 'text-[var(--color-muted)]'}`}>
            {isClosed
              ? fmtUsd(goal.bonusUsd)
              : hit
                ? fmtUsd(goal.bonusUsd)
                : fmtUsd(goal.bonusIfHitUsd)}
          </div>
          <div className="text-xs uppercase tracking-wide text-[var(--color-muted)]">
            {isClosed ? 'paid' : hit ? 'earned' : 'if hit'}
          </div>
        </div>
      </div>
    </Card>
  );
}

function TeamRetentionCard({
  result,
  isFullTime,
  eligible,
}: {
  result: TeamRetentionResponse;
  isFullTime: boolean;
  eligible: boolean;
}): React.ReactElement {
  const hit = result.status === 'hit';
  const tierBonus = isFullTime ? result.bonusFtUsd : result.bonusPtUsd;
  const earned = hit && eligible;
  return (
    <Card className="!p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-[var(--color-muted)]">
            {GOAL_TITLE.team_retention}
          </div>
          <div className="mt-1 text-sm text-[var(--color-body)]">{GOAL_TARGET.team_retention}</div>
        </div>
        <StatusPill status={eligible ? result.status : 'ineligible'} />
      </div>
      <div className="mt-4 flex items-end justify-between gap-4">
        <div>
          <div className="text-2xl font-semibold text-[var(--color-header)]">
            {fmtPct(result.retentionPct)}
          </div>
          <div className="mt-1 text-xs text-[var(--color-muted)]">
            {!eligible ? 'Need 20 hrs tracked in each month of the quarter' : result.detail}
          </div>
        </div>
        <div className="text-right">
          <div className={`text-xl font-bold ${earned ? 'text-emerald-700' : 'text-[var(--color-muted)]'}`}>
            {fmtUsd(tierBonus)}
          </div>
          <div className="text-xs uppercase tracking-wide text-[var(--color-muted)]">
            {earned ? 'earned' : 'if hit'}
          </div>
        </div>
      </div>
    </Card>
  );
}

function monthOptions(): { key: string; label: string }[] {
  const now = new Date();
  const out: { key: string; label: string }[] = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    out.push({ key, label });
  }
  return out;
}

function quarterOptions(): { key: string; label: string }[] {
  const now = new Date();
  const out: { key: string; label: string }[] = [];
  const currentQ = Math.floor(now.getMonth() / 3) + 1;
  let y = now.getFullYear();
  let q = currentQ;
  for (let i = 0; i < 8; i++) {
    out.push({ key: `${y}-Q${q}`, label: `${y} Q${q}` });
    q -= 1;
    if (q < 1) {
      q = 4;
      y -= 1;
    }
  }
  return out;
}

export function GoalsPage(): React.ReactElement {
  useDocumentTitle('My goals');
  const { agent } = useAuth();
  const months = useMemo(monthOptions, []);
  const quarters = useMemo(quarterOptions, []);
  const [monthKey, setMonthKey] = useState(months[0]?.key ?? '');
  const [quarterKey, setQuarterKey] = useState(quarters[0]?.key ?? '');
  const [data, setData] = useState<MyGoalsResponse | null>(null);
  const [team, setTeam] = useState<TeamRetentionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    const params = new URLSearchParams();
    if (monthKey) params.set('month', monthKey);
    if (quarterKey) params.set('quarter', quarterKey);
    apiFetch<MyGoalsResponse>(`/api/goals/me?${params.toString()}`)
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : 'Failed to load goals.');
      });
    return () => {
      cancelled = true;
    };
  }, [monthKey, quarterKey]);

  useEffect(() => {
    let cancelled = false;
    if (!quarterKey) return;
    const params = new URLSearchParams({ quarter: quarterKey });
    apiFetch<TeamRetentionResponse>(`/api/goals/team?${params.toString()}`)
      .then((r) => {
        if (!cancelled) setTeam(r);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : 'Failed to load team goal.');
      });
    return () => {
      cancelled = true;
    };
  }, [quarterKey]);

  if (!agent) return <p>Loading…</p>;

  const monthlyTotal = data?.monthlyGoals.reduce((s, g) => s + g.bonusUsd, 0) ?? 0;
  const monthlyMax = data?.monthlyGoals.reduce((s, g) => s + g.bonusIfHitUsd, 0) ?? 0;
  const teamEligible =
    data
      ? Object.values(data.trackedMinutesByMonth).every((m) => m >= 1200) &&
        Object.keys(data.trackedMinutesByMonth).length === 3
      : false;
  const teamTier = data && team ? (data.isFullTime ? team.bonusFtUsd : team.bonusPtUsd) : 0;
  const teamEarned = team && team.status === 'hit' && teamEligible ? teamTier : 0;
  const quarterlyTotal = (data?.quarterlyGoals.reduce((s, g) => s + g.bonusUsd, 0) ?? 0) + teamEarned;
  const quarterlyMax = (data?.quarterlyGoals.reduce((s, g) => s + g.bonusIfHitUsd, 0) ?? 0) + teamTier;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-[var(--color-header)]">My goals</h1>
        <p className="mt-1 text-[var(--color-muted)]">
          Bonuses are <strong>hit or miss</strong> — no pro-rating. You need to track at least 20
          hours in a month to qualify for that month's bonuses, and 20 hours in each of the three
          months of the quarter to qualify for the quarterly bonuses (CSAT, project on-time,
          client retention).
        </p>
      </div>

      {error && (
        <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
          <p className="text-sm text-[var(--color-danger)]">{error}</p>
        </Card>
      )}

      <section className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-[var(--color-header)]">Monthly</h2>
            {data && (
              <p className="mt-1 text-sm text-[var(--color-muted)]">
                Tracked this month:{' '}
                <strong>
                  {((data.trackedMinutesByMonth[monthKey] ?? 0) / 60).toFixed(1)}h
                </strong>{' '}
                {(data.trackedMinutesByMonth[monthKey] ?? 0) >= 1200 ? (
                  <span className="text-emerald-700">(eligible)</span>
                ) : (
                  <span className="text-amber-700">(need 20h to qualify)</span>
                )}
                {data.isPeriodClosed.monthly && (
                  <span className="ml-2 rounded bg-gray-100 px-2 py-0.5 text-xs">CLOSED</span>
                )}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Select
              label="Month"
              value={monthKey}
              onChange={(e) => setMonthKey(e.target.value)}
            >
              {months.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </Select>
            <div className="rounded bg-[var(--color-surface)] px-3 py-2 text-right">
              <div className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Earned / Max</div>
              <div className="text-base font-semibold text-[var(--color-header)]">
                {fmtUsd(monthlyTotal)} / {fmtUsd(monthlyMax)}
              </div>
            </div>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {!data ? (
            <p className="text-[var(--color-muted)]">Loading…</p>
          ) : data.monthlyGoals.length === 0 ? (
            <Card>
              <p className="text-sm text-[var(--color-muted)]">No monthly goals configured.</p>
            </Card>
          ) : (
            data.monthlyGoals.map((g) => (
              <GoalCard key={g.goalKey} goal={g} isClosed={data.isPeriodClosed.monthly} />
            ))
          )}
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-[var(--color-header)]">Quarterly</h2>
            {data && (
              <p className="mt-1 text-sm text-[var(--color-muted)]">
                Tracked this quarter:{' '}
                {Object.entries(data.trackedMinutesByMonth).map(([k, mins], i) => (
                  <span key={k}>
                    {i > 0 ? ' • ' : ''}
                    {k}: <strong>{(mins / 60).toFixed(1)}h</strong>
                    {mins >= 1200 ? '' : ' ⚠'}
                  </span>
                ))}
                {data.isPeriodClosed.quarterly && (
                  <span className="ml-2 rounded bg-gray-100 px-2 py-0.5 text-xs">CLOSED</span>
                )}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Select
              label="Quarter"
              value={quarterKey}
              onChange={(e) => setQuarterKey(e.target.value)}
            >
              {quarters.map((q) => (
                <option key={q.key} value={q.key}>
                  {q.label}
                </option>
              ))}
            </Select>
            <div className="rounded bg-[var(--color-surface)] px-3 py-2 text-right">
              <div className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Earned / Max</div>
              <div className="text-base font-semibold text-[var(--color-header)]">
                {fmtUsd(quarterlyTotal)} / {fmtUsd(quarterlyMax)}
              </div>
            </div>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {!data ? (
            <p className="text-[var(--color-muted)]">Loading…</p>
          ) : (
            <>
              {data.quarterlyGoals.map((g) => (
                <GoalCard key={g.goalKey} goal={g} isClosed={data.isPeriodClosed.quarterly} />
              ))}
              {team && data && (
                <TeamRetentionCard result={team} isFullTime={data.isFullTime} eligible={teamEligible} />
              )}
            </>
          )}
        </div>
      </section>

      <Card className="!p-4 text-xs text-[var(--color-muted)]">
        Closed periods are final and read from the snapshot taken at close time — admin edits to
        bonus amounts after close don't change history. Need a correction? Ask an admin to reopen
        the period.{' '}
        <Link to="/admin/goals" className="text-[var(--color-t3-blue)] hover:underline">
          Admin → Goals & Bonuses
        </Link>
      </Card>
    </div>
  );
}
